import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as providerAccountActions from '@redux/modules/providerAccount'

import defaultFormState, { requiredFields } from '@models/providerAccount'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { ModuleState } from '@redux/modules/types'
import type { ProviderAccountModel, ProviderAccountRequestOptions } from '@models/types'

export const generateUrls = (providerAccount?: Partial<ProviderAccountModel>) => {
  const { agency_id } = providerAccount || {}

  return {
    editProviderAccountUrl: `#/systemManager/providerAgencies/${agency_id}/edit`,
    editProviderAccountRelationsUrl: `#/systemManager/providerAgencies/${agency_id}/edit/relations`,
    providerAccountsIndexUrl: '#/systemManager/providerAgencies',
  }
}

type CreateOrUpdateProviderAccountParams = {
  dispatch: AppDispatch,
  providerAccount: ProviderAccountModel,
  providerAccountParams: Partial<ProviderAccountModel>,
  requestOptions?: ProviderAccountRequestOptions,
}

const createOrUpdateProviderAccount = (params: CreateOrUpdateProviderAccountParams) => {
  const {
    dispatch, providerAccount, providerAccountParams, requestOptions,
  } = params
  const { createOrUpdateProviderAccount: createOrUpdateFn } = providerAccountActions

  const createOrUpdatedParams = {
    ...providerAccount,
    ...providerAccountParams,
  }

  return dispatch(createOrUpdateFn(createOrUpdatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editProviderAccountUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: editProviderAccountUrl,
      }
    }

    return response
  })
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useProviderAccountForm(
  providerAccount: Partial<ProviderAccountModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const providerAccountForm = useForm(
    defaultFormState,
    { entity: providerAccount, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [providerAccount.id, providerAccount.cache_key],
  )

  return {
    ...providerAccountForm,
  }
}

export const useRelations = (providerAccount: Partial<ProviderAccountModel> = {}) => {
  const { agency_id, provider_id } = providerAccount

  const { agencies, brands, providers } = useSelector(reduxState => reduxState.entities)

  const agency = agency_id && agencies[agency_id] ? agencies[agency_id] : {}
  // @ts-expect-error TS2339
  const brand = agency?.id && brands[agency?.brand_id] ? brands[agency?.brand_id] : {}
  const provider = provider_id && providers[provider_id] ? providers[provider_id] : {}

  return {
    agency,
    brand,
    provider,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ProviderAccountRequestOptions,
}

function useProviderAccount(initEntity: Partial<ProviderAccountModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: providerAccount }: { entity: ProviderAccountModel } = useLatestEntity(initEntity, 'providerAccounts')
  const { id } = providerAccount

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency, brand, provider } = useRelations(providerAccount)

  useReduxAction(
    'providerAccounts',
    'loadProviderAccount',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, loadedIds, loaded, loading, updating,
  } = useSelector(reduxState => reduxState.providerAccounts)

  return {
    agency,
    brand,
    callbacks: {
      addAgency: () => {
        launchModal({
          callbacks,
          modalKey: 'AddAgencyToProviderAccountModal',
          payload: { providerAccount },
        })
      },
      createOrUpdateProviderAccount: (
        providerAccountParams: Partial<ProviderAccountModel>,
        entityOptions: ProviderAccountRequestOptions,
      ) => (
        createOrUpdateProviderAccount({
          providerAccount, providerAccountParams, dispatch, requestOptions: entityOptions,
        })
      ),
      createOrEditProviderAccount: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditProviderAccountModal',
        payload: { providerAccount },
      }),
    },
    creating,
    loaded,
    loading: loading && !loadedIds.includes(providerAccount.id),
    providerAccount,
    provider,
    updating,
    urls: generateUrls(providerAccount),
  }
}

export default useProviderAccount
